/* @jsx jsx */
import { jsx } from '@emotion/react';

import classNames from 'classnames';

import { typography2 } from '@coursera/cds-core';

import ProfileImage from 'bundles/phoenix/components/ProfileImage';

import _t from 'i18n!nls/classmates';

type CourseRole =
  | 'INSTRUCTOR'
  | 'MENTOR'
  | 'COURSE_ASSISTANT'
  | 'UNIVERSITY_ADMIN'
  | 'DATA_COORDINATOR'
  | 'TOP_CONTRIBUTOR'
  | 'LEARNER';

type ProfileImageProps = {
  fullName: string;
  profileImageUrl?: string;
  alt?: string;
  ariaHidden?: boolean;
};

type RoleProps = {
  courseRole?: CourseRole;
};

type ProfileImageWithRoleProps = ProfileImageProps & RoleProps;

function getCourseRoleIconInfo({ courseRole }: { courseRole?: CourseRole }) {
  switch (courseRole) {
    case 'INSTRUCTOR':
      return {
        letter: _t('I'),
        css: { backgroundColor: 'var(--cds-color-blue-700)', color: 'var(--cds-color-white-0)' },
      };
    case 'COURSE_ASSISTANT':
    case 'MENTOR':
      return {
        letter: _t('M'),
        css: { backgroundColor: 'var(--cds-color-purple-950)', color: 'var(--cds-color-white-0)' },
      };
    case 'UNIVERSITY_ADMIN':
    case 'DATA_COORDINATOR':
      return {
        letter: _t('S'),
        css: { backgroundColor: 'var(--cds-color-purple-950)', color: 'var(--cds-color-white-0)' },
      };
    case 'TOP_CONTRIBUTOR':
      return {
        letter: _t('C'),
        css: { backgroundColor: 'var(--cds-color-purple-950)', color: 'var(--cds-color-white-0)' },
      };
    default:
      return {};
  }
}

function ProfileImageWithRole({
  courseRole,
  ariaHidden = false,
  fullName,
  profileImageUrl,
  alt,
}: ProfileImageWithRoleProps) {
  const courseRoleInfo = getCourseRoleIconInfo({ courseRole });
  const className = classNames('course-role-icon', 'caption-text', 'horizontal-box', 'align-items-absolute-center');

  return (
    <span
      css={{
        '.rc-ProfileImage div.c-profile-image.c-profile-initials': {
          backgroundColor: `var(--cds-color-blue-50)`,
          '& p': {
            color: `var(--cds-color-grey-975)`,
            ...typography2.bodyPrimary,
          },
        },
        '.course-role-icon': {
          borderRadius: '100%',
          width: '18px',
          height: '18px',
          position: 'absolute',
          marginLeft: 'var(--cds-spacing-300)',
          marginTop: '-14px',
        },
      }}
      className="rc-ProfileImageWithRole"
    >
      <ProfileImage
        fullName={fullName}
        profileImageUrl={profileImageUrl}
        alt={alt}
        ariaHidden={ariaHidden}
        width={36}
        height={36}
      />
      {courseRoleInfo.letter && (
        <span className={className} css={courseRoleInfo.css}>
          {courseRoleInfo.letter}
        </span>
      )}
    </span>
  );
}

export default ProfileImageWithRole;
